import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/lib/brand";
import {
  LayoutDashboard, Users, Users2, UserPlus, ClipboardList, CalendarDays,
  Plane, Wallet, Target, GraduationCap, Boxes, LogOut, BarChart3,
  FileText, Bell, ShieldCheck, Building2, ChevronDown, BookOpen, Sparkles,
  UserRound, KeyRound,
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";

const NAV = [
  { group: "Workspace", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["*"] },
  ]},
  { group: "People", items: [
    { to: "/employees", label: "Employees", icon: Users, roles: ["hr", "manager", "team_lead", "super_admin", "recruiter"] },
    { to: "/team-mapping", label: "Team Mapping", icon: Users2, roles: ["hr", "manager", "team_lead", "super_admin"] },
    { to: "/my-team", label: "My Team", icon: Users2, roles: ["manager", "team_lead"] },
    { to: "/recruitment", label: "Recruitment", icon: ClipboardList, roles: ["hr", "recruiter", "manager", "team_lead"] },
    { to: "/onboarding", label: "Onboarding", icon: UserPlus, roles: ["hr", "manager", "team_lead"] },
    { to: "/my/onboarding", label: "My Onboarding", icon: UserPlus, roles: ["employee", "hr", "manager", "team_lead"] },
    { to: "/onboarding/wizard", label: "Joiner Wizard", icon: Sparkles, roles: ["hr", "manager", "team_lead"] },
  ]},
  { group: "Time", items: [
    { to: "/attendance", label: "Attendance", icon: CalendarDays, roles: ["*"] },
    { to: "/leave", label: "Leave", icon: Plane, roles: ["*"] },
    { to: "/ot", label: "Over Time", icon: CalendarDays, roles: ["*"] },
    { to: "/holidays", label: "Holidays", icon: CalendarDays, roles: ["*"] },
  ]},
  { group: "Performance & Growth", items: [
    { to: "/performance", label: "Performance", icon: Target, roles: ["*"] },
    { to: "/production", label: "Production", icon: ClipboardList, roles: ["*"] },
    { to: "/pip", label: "PIP", icon: Target, roles: ["hr", "manager", "team_lead"] },
    { to: "/training", label: "Training", icon: GraduationCap, roles: ["*"] },
    { to: "/learning", label: "Learning (LMS)", icon: BookOpen, roles: ["*"] },
  ]},
  { group: "Operations", items: [
    { to: "/payroll", label: "Payroll", icon: Wallet, roles: ["hr", "finance", "manager", "team_lead", "it"] },
    { to: "/my/payslips", label: "My Payslips", icon: Wallet, roles: ["*"] },
    { to: "/assets", label: "Assets", icon: Boxes, roles: ["hr", "it", "manager", "team_lead"] },
    { to: "/exit", label: "Exit", icon: LogOut, roles: ["hr", "manager", "team_lead"] },
    { to: "/my/resign", label: "Submit Resignation", icon: LogOut, roles: ["employee"] },
  ]},
  { group: "Insights", items: [
    { to: "/reports", label: "Reports", icon: BarChart3, roles: ["hr", "finance", "manager", "team_lead"] },
    { to: "/documents", label: "Documents", icon: FileText, roles: ["hr", "manager", "team_lead", "super_admin", "finance", "it", "recruiter"] },
    { to: "/notifications", label: "Notifications", icon: Bell, roles: ["*"] },
  ]},
  { group: "System", items: [
    { to: "/admin", label: "Administration", icon: ShieldCheck, roles: ["super_admin"] },
  ]},
];

function canSee(role, allowed) {
  if (allowed.includes("*")) return true;
  if (role === "super_admin") return true;
  return allowed.includes(role);
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#0F172A] text-slate-200 border-r border-slate-800 flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-800">
          <img src={BRAND.logoUrl} alt="Solemnity" className="h-8 w-8 object-contain rounded bg-white p-0.5" />
          <div className="leading-tight">
            <div className="font-display font-semibold text-white text-sm">Solemnity</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">HRMS</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 scroll-shadow">
          {NAV.map((g) => {
            const visible = g.items.filter((i) => canSee(user?.role, i.roles));
            if (visible.length === 0) return null;
            return (
              <div key={g.group}>
                <div className="hr-section-title">{g.group}</div>
                {visible.map((i) => (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    data-testid={`nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `hr-sidebar-link ${isActive ? "active" : ""}`
                    }
                  >
                    <i.icon className="h-4 w-4" />
                    <span>{i.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-slate-800">
            <div className="h-8 w-8 rounded-full bg-[#1E88E5] text-white grid place-items-center text-xs font-semibold">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 capitalize truncate">
                {user?.role?.replace("_", " ")}
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#F8FAFC]">
        <header className="h-16 sticky top-0 z-30 backdrop-blur bg-white/90 border-b border-slate-200 px-4 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="sidebar-toggle"
              className="lg:hidden p-2 -ml-2"
              onClick={() => setOpen((v) => !v)}
            >
              <Building2 className="h-5 w-5" />
            </button>
            <div className="hidden md:block">
              <div className="text-[11px] uppercase tracking-wider text-gray-400">Workspace</div>
              <div className="text-sm font-medium text-gray-700">Solemnity IT Healthcare Solutions Pvt Ltd</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="notifications-btn"
              onClick={() => navigate("/notifications")}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
            >
              <Bell className="h-4 w-4" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-100"
                >
                  {user?.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt={user.name || "avatar"}
                      className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"
                      data-testid="user-menu-avatar"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-[#1E88E5] text-white grid place-items-center text-xs font-semibold" data-testid="user-menu-initials">
                      {initials}
                    </div>
                  )}
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-xs font-medium text-slate-700 truncate max-w-[140px]">{user?.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{user?.role?.replace("_", " ")}</div>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <div className="flex items-center gap-3">
                    {user?.profile_picture ? (
                      <img src={user.profile_picture} alt="" className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-[#1E88E5] text-white grid place-items-center text-sm font-semibold">{initials}</div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{user?.name}</div>
                      <div className="text-[11px] text-gray-500 truncate">{user?.email}</div>
                      {user?.employee_id ? <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{user.employee_id}</div> : null}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="user-menu-profile" onClick={() => navigate("/profile")}>
                  <UserRound className="h-4 w-4 mr-2" />My Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="user-menu-account" onClick={() => navigate("/profile?tab=settings")}>
                  <ShieldCheck className="h-4 w-4 mr-2" />Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="user-menu-password" onClick={() => navigate("/profile?tab=password")}>
                  <KeyRound className="h-4 w-4 mr-2" />Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="user-menu-logout" onClick={logout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-6 md:p-8 min-w-0">{children}</main>
      </div>
    </div>
  );
}
